import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { TableContainer, Paper, Grid, TextField, Box, Tooltip, IconButton } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Autocomplete from "@mui/material/Autocomplete";
import PropTypes from "prop-types";
import {
  StyledButton,
  StyledSnackbar,
  StyledAlert,
  inputStyle,
  StyledButtonC,
  StyledTypography,
  StyledTableContainer,
  StyledAgGridContainer,
  ViewButton,
  DeleteButton,
} from "components/StyledComponents/styledComponents.js";
import { CustomPaper } from "components/Styles/globalStyles";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import ResponsiveTabs from "components/StyledComponents/styledComponents";
import Cookie from "js-cookie";
import URLs from "constants/urls";
import { subDialogContentStyle, dialogContentTextStyle } from "components/Styles/approvalStyles";
import { useMaterialUIController } from "context";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { CircularProgress } from "@mui/material";

export default function EmployeeLeave() {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const hrms_token = Cookie.get("hrms_token");

  // State variables
  const [employeeName, setEmployeeName] = useState("Loading...");
  const [hiringDate, setHiringDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState(0);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [employeeRecordId, setEmployeeRecordId] = useState(null);
  const [leaveType, setLeaveType] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [numberOfDays, setNumberOfDays] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [activeTab, setActiveTab] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [calendarYears, setCalendarYears] = useState([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState(null);
  const MInput = inputStyle();
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [remarksDialogOpen, setRemarksDialogOpen] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState("");
  const tabs = [{ label: "Leave Applications" }, { label: "Apply Leave" }];
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [months, setMonths] = useState([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState("");
  const [selectedPayPeriodId, setSelectedPayPeriodId] = useState("");

  const fetchMonths = async () => {
    try {
      const response = await fetch(`${URLs.hrmsGetPayPeriods}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch pay periods");
      }

      const data = await response.json();
      setMonths(data);

      if (Array.isArray(data) && data.length > 0) {
        const firstPeriod = data[0];
        setSelectedPayPeriod(firstPeriod.payPeriodName);
        setSelectedPayPeriodId(firstPeriod.payPeriodId);
      }
    } catch (error) {
      console.error("Error fetching pay periods:", error);
      setAlert("Failed to fetch pay periods", "error");
    }
  };

  const handleDisplay = () => {
    if (!selectedPayPeriod || !selectedPayPeriodId) {
      setAlert("Please select a pay period", "error");
      return;
    }
    fetchLeaveApplications(selectedPayPeriodId); // ← only fetches when button clicked
  };

  const fetchEmployeesByCompany = async () => {
    if (!user) {
      setAlert("Please Login Again", "error");
      return;
    }

    try {
      setLoadingEmployees(true);
      const url = `${URLs.employeeNamesByCompanyBase}?companyId=${user.companyId}`;
      console.log("→ Fetching employees from:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: hrms_token ? `Bearer ${hrms_token}` : "",
        },
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        console.error("Employees fetch failed:", response.status, errorText);
        setAlert("Failed to load employees", "error");
        setEmployees([]);
        return;
      }

      let data = await response.json();
      console.log("Raw employees data:", data);
      const normalizedEmployees = (Array.isArray(data) ? data : []).map((emp) => {
        const normalized = { ...emp };
        if ("employeeeRecordId" in normalized && !("employeeRecordId" in normalized)) {
          normalized.employeeRecordId = normalized.employeeeRecordId;
          delete normalized.employeeeRecordId;
        }
        if (normalized.employeeName) {
          normalized.employeeName = normalized.employeeName.trim();
        }

        return normalized;
      });

      console.log("Normalized employees count:", normalizedEmployees.length);
      setEmployees(normalizedEmployees);
    } catch (err) {
      console.error("Employees API error:", err);
      setAlert("Could not load employees", "error");
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };
  const fetchEmployeeDetails = async (employeeId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${URLs.getEmpPayRollData}${employeeId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch employee details");
      }
      const data = await response.json();
      if (data) {
        setEmployeeName(data.employeeName || user?.employeeName || "");
        setHiringDate(data.hiringDate || "");
        setEmployeeRecordId(data.employeeRecordId);
        return {
          recordId: data.employeeRecordId,
          employeeName: data.employeeName,
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching employee details:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      setLoading(true);
      const response = await fetch(URLs.hrmsFetchEmploeeLeaveTypeUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch leave types");
      }

      const data = await response.json();
      const formattedLeaveTypes = data.map((leave) => ({
        label: leave.leaveName,
        value: leave.leaveName.replace(/\s+/g, "").substring(0, 2).toUpperCase(),
        id: leave.leaveId,
      }));
      setLeaveTypes(formattedLeaveTypes);
      return formattedLeaveTypes; // Return for immediate use
    } catch (error) {
      console.error("Error fetching leave types:", error);
      setAlert("Failed to fetch leave types", "error");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarYears = async () => {
    try {
      const response = await fetch(URLs.hrmsCalendarYearsUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch calendar years");
      }

      const data = await response.json();
      setCalendarYears(data);
      // Set the first calendar year as default
      if (data && data.length > 0) {
        setSelectedCalendarId(data[0].calendarYearId);
      }
    } catch (error) {
      console.error("Error fetching calendar years:", error);
      setAlert("Failed to fetch calendar years", "error");
    }
  };

  const fetchLeaveBalance = async (empRecordId, leaveId, calendarId) => {
    if (!empRecordId || !leaveId || !calendarId) return;

    try {
      console.log("Fetching leave balance with params:", { empRecordId, leaveId, calendarId });
      const response = await fetch(
        `${URLs.hrmsLeaveBalanceUrl}?employeeRecordId=${empRecordId}&leaveId=${leaveId}&calendarYearId=${calendarId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${hrms_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch leave balance");
      }

      const data = await response.json();
      console.log("Leave balance response:", data);
      setLeaveBalance(data.balance || data || 0);
    } catch (error) {
      console.error("Error fetching leave balance:", error);
      setLeaveBalance(0);
    }
  };

  const getLeaveTypeName = (leaveId, types = leaveTypes) => {
    if (!leaveId || !types?.length) return "Unknown";
    const leaveType = types.find((type) => type.id === leaveId);
    return leaveType ? leaveType.label : "Unknown";
  };

  const fetchLeaveApplications = async (payPeriodId = selectedPayPeriodId) => {
    if (!employeeRecordId) {
      console.log("No employee record ID available");
      return;
    }

    try {
      setLoading(true);
      console.log("Fetching leave applications for employee record ID:", employeeRecordId);

      const response = await fetch(`${URLs.empLeaveDetailsByPayPeriod}/${payPeriodId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.log("API Error Status:", response.status);
        console.log("API Error Message:", errorText);
        throw new Error("Failed to fetch leave applications");
      }

      const data = await response.json();
      console.log("Leave applications data:", data);

      if (!Array.isArray(data)) {
        throw new Error("Invalid data format received");
      }

      const formattedData = data.map((item) => ({
        empLeaveId: item.empLeaveId,
        fromDate: formatDate(item.fromDate),
        toDate: formatDate(item.toDate),
        employeeName: item.employeeName || employeeName,
        dayCount: item.dayCount,
        leaveType: getLeaveTypeName(item.leaveId),
        status:
          item.approvedStatus?.trim() === "P"
            ? "Pending"
            : item.approvedStatus?.trim() === "A"
            ? "Approved"
            : item.approvedStatus?.trim() === "R"
            ? "Rejected"
            : "Unknown",
        remarks: item.remarks || "",
        approvedBy: item.approvedBy ? item.approvedBy.trim() : "",
        rawData: item,
      }));

      console.log("Formatted data:", formattedData);
      setTableData(formattedData);
    } catch (error) {
      console.error("Error fetching leave applications:", error);
      setAlert("Failed to fetch leave applications", "error");
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  useEffect(() => {
    if (fromDate && toDate) {
      const days = calculateDays(fromDate, toDate);
      setNumberOfDays(days);
    }
  }, [fromDate, toDate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!employeeRecordId || !selectedCalendarId) {
      setAlert("Employee Record ID or Calendar Year is missing", "error");
      return;
    }

    if (!fromDate || !toDate) {
      setAlert("Please select both from and to dates", "error");
      return;
    }

    if (fromDate > toDate) {
      setAlert("From date cannot be after to date", "error");
      return;
    }

    const selectedLeaveType = leaveTypes.find((type) => type.value === leaveType);
    if (!selectedLeaveType) {
      setAlert("Please select a valid leave type", "error");
      return;
    }

    const formatDateForAPI = (date) => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const leaveData = {
      empLeaveId: editMode ? editItemId : 0,
      calendarId: selectedCalendarId,
      employeeRecordID: parseInt(employeeRecordId),
      leaveId: selectedLeaveType.id,
      dayCount: parseInt(numberOfDays),
      fromDate: formatDateForAPI(fromDate),
      toDate: formatDateForAPI(toDate),
      remarks: remarks || "Leave Request",
      approvedStatus: "P",
      approvedBy: "",
      employeeName: employeeName,
    };

    console.log("Submitting leave data:", leaveData);

    try {
      setSubmitting(true);
      const response = await fetch(
        editMode ? `${URLs.empUpdatesLeaves}/${editItemId}` : URLs.hrmsInsertEmployeeLeaveUrl,
        {
          method: editMode ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${hrms_token}`,
          },
          body: JSON.stringify(leaveData),
        }
      );

      const responseData = await response.json();
      console.log("Leave submission response:", responseData);

      if (!response.ok) {
        throw new Error(
          responseData.message || `Failed to ${editMode ? "update" : "submit"} leave request`
        );
      }

      // await fetchLeaveApplications();  // ← remove this line
      setTableData([]); // ← clear table so user must click Display to see data
      resetForm();
      setAlert(`Leave request successfully ${editMode ? "updated" : "submitted"}`, "success");
      setActiveTab(0);
    } catch (error) {
      console.error("Submission error:", error);
      setAlert(
        error.message || `Failed to ${editMode ? "update" : "submit"} leave request`,
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const resetForm = () => {
    setLeaveType("");
    setFromDate(null);
    setToDate(null);
    setRemarks("");
    setNumberOfDays(0);
    if (!user?.employeeId) {
      setEmployeeName("");
      setHiringDate("");
      setEmployeeRecordId(null);
    }
    setEditMode(false);
    setEditItemId(null);
  };

  const handleEdit = (rowData) => {
    const currentDate = new Date();
    const toDate = new Date(rowData.rawData.toDate);

    if (currentDate > toDate) {
      setAlert("Cannot edit leave request after To Date has passed", "error");
      return;
    }

    setEditMode(true);
    setEditItemId(rowData.empLeaveId);
    setLeaveType(leaveTypes.find((type) => type.label === rowData.leaveType)?.value || "");
    setFromDate(new Date(rowData.rawData.fromDate));
    setToDate(new Date(rowData.rawData.toDate));
    setRemarks(rowData.remarks);
    setNumberOfDays(rowData.dayCount);
    setActiveTab(1);
  };

  const handleOpenDialog = (rowData) => {
    const currentDate = new Date();
    const toDate = new Date(rowData.rawData.toDate);

    if (currentDate > toDate) {
      setAlert("Cannot delete leave request after To Date has passed", "error");
      return;
    }

    setDeleteRow(rowData);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDeleteRow(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${URLs.deleteEmpLeaves}/${deleteRow.empLeaveId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${hrms_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete leave application");
      }

      setAlert("Leave application deleted successfully", "success");
      await fetchLeaveApplications();
    } catch (error) {
      console.error("Delete error:", error);
      setAlert("Failed to delete leave application", "error");
    } finally {
      handleCloseDialog();
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const ActionCellRenderer = (props) => {
    const rowData = props.data;
    const currentDate = new Date();
    const toDate = new Date(rowData.rawData.toDate);

    ActionCellRenderer.propTypes = {
      data: PropTypes.shape({
        rawData: PropTypes.shape({
          toDate: PropTypes.string.isRequired, // Validate 'toDate' field
        }).isRequired,
      }).isRequired,
    };

    // Only allow modifications if status is Pending AND current date hasn't passed the To Date
    const canModify = rowData.status === "Pending" && currentDate <= toDate;

    return (
      <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
        {canModify && (
          <>
            <Tooltip title="Edit">
              <IconButton
                onClick={() => handleEdit(rowData)}
                size="small"
                sx={{ color: "#1976d2" }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={() => handleOpenDialog(rowData)}
                size="small"
                sx={{ color: "#d32f2f" }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
        {rowData.status === "Pending" && !canModify && (
          <Tooltip title="Cannot modify leave request after To Date has passed">
            <span style={{ color: "#666", fontSize: "0.875rem" }}>Expired</span>
          </Tooltip>
        )}
      </Box>
    );
  };

  const ViewRemarksRenderer = (props) => (
    <div style={{ cursor: "pointer", display: "flex", justifyContent: "center" }}>
      <Tooltip title="View Remarks">
        <IconButton
          onClick={() => {
            setSelectedRemarks(props.value);
            setRemarksDialogOpen(true);
          }}
          size="small"
        >
          <VisibilityIcon style={{ color: "#1a73e8" }} />
        </IconButton>
      </Tooltip>
    </div>
  );

  ViewRemarksRenderer.propTypes = {
    value: PropTypes.string.isRequired, // Add validation for 'value'
  };

  const columnDefs = [
    {
      headerName: "Employee Name",
      field: "employeeName",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: {
        textAlign: "left",
        paddingLeft: "20px",
      },
    },
    {
      headerName: "Leave Type",
      field: "leaveType",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: {
        textAlign: "left",
        paddingLeft: "20px",
      },
    },
    {
      headerName: "From Date",
      field: "fromDate",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: {
        textAlign: "left",
        paddingLeft: "20px",
      },
    },
    {
      headerName: "To Date",
      field: "toDate",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: {
        textAlign: "left",
        paddingLeft: "20px",
      },
    },
    {
      headerName: "Days",
      field: "dayCount",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: {
        textAlign: "left",
        paddingLeft: "20px",
      },
    },
    {
      headerName: "Reason",
      field: "remarks",
      cellRenderer: ViewRemarksRenderer,
      width: 100,
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
    {
      headerName: "Status",
      field: "status",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: (params) => ({
        textAlign: "left",
        paddingLeft: "20px",
        color:
          params.value === "Approved"
            ? "#2E7D32"
            : params.value === "Rejected"
            ? "#D32F2F"
            : "#ED6C02",
      }),
    },

    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: ActionCellRenderer,
      width: 120,
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
  ];

  useEffect(() => {
    const initializeData = async () => {
      if (!user?.employeeId) {
        console.log("No user ID available");
        return;
      }

      try {
        // Fetch calendar years first
        await fetchCalendarYears();

        // Then fetch leave types
        await fetchLeaveTypes();
        // Then fetch pay periods
        await fetchMonths();
        // Next fetch employees to ensure we have the latest data before fetching employee details
        await fetchEmployeesByCompany();

        // Then fetch employee details and get record ID
        const employeeData = await fetchEmployeeDetails(user.employeeId);
        // Then fetch employee details and get record ID
        await fetchEmployeeDetails(user.employeeId);

        // Finally fetch leave applications if we have the record ID
        // if (employeeData?.recordId) {
        //   await fetchLeaveApplications();
        // } else {
        //   setAlert("Could not fetch employee record ID", "error");
        // }
      } catch (error) {
        console.error("Error initializing data:", error);
        setAlert("Error loading data. Please refresh the page.", "error");
      }
    };

    initializeData();
  }, [user]);

  // Effect to fetch leave balance when leave type changes
  useEffect(() => {
    if (employeeRecordId && leaveType && selectedCalendarId) {
      const selectedLeaveType = leaveTypes.find((type) => type.value === leaveType);
      if (selectedLeaveType) {
        fetchLeaveBalance(employeeRecordId, selectedLeaveType.id, selectedCalendarId);
      }
    }
  }, [employeeRecordId, leaveType, selectedCalendarId]);

  // useEffect(() => {
  //   if (employeeRecordId) {
  //     fetchLeaveApplications();
  //   }
  // }, [employeeRecordId]);

  const handleRefresh = () => {
    fetchLeaveApplications();
  };

  return (
    <DashboardLayout>
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", mt: 0 }}>
        <ResponsiveTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleChange}
          labelColor="secondary"
          bgColor="#e0f7fa"
        />
        {activeTab === 0 && (
          <Box
            sx={{
              overflowX: "auto",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              mt: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center", pt: 2 }}>
              <TextField
                select
                label="Select Pay Period"
                value={selectedPayPeriod}
                onChange={(e) => {
                  const selectedPeriodName = e.target.value;
                  setSelectedPayPeriod(selectedPeriodName);
                  const found = months.find((p) => p.payPeriodName === selectedPeriodName);
                  if (found) {
                    setSelectedPayPeriodId(found.payPeriodId);
                  } else {
                    setSelectedPayPeriodId("");
                  }
                }}
                //sx={{ minWidth: 200, ...MInput.b }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Select a pay period</option>
                {months.map((period) => (
                  <option key={period.payPeriodId} value={period.payPeriodName}>
                    {period.payPeriodName}
                  </option>
                ))}
              </TextField>
              <StyledButton onClick={handleDisplay}>Display</StyledButton>
            </Box>

            <StyledAgGridContainer
              component={Paper}
              className="ag-theme-alpine"
              sx={{ flexGrow: 1 }}
            >
              <AgGridReact
                rowData={tableData}
                columnDefs={columnDefs}
                pagination={true}
                paginationPageSize={10}
                suppressCellFocus={true}
                defaultColDef={{
                  resizable: true,
                  filter: true,
                }}
              />
            </StyledAgGridContainer>
          </Box>
        )}
        {activeTab === 1 && (
          <StyledTableContainer
            container
            spacing={1}
            sx={{
              display: "flex",
              backgroundColor: "#ffffff",
              minHeight: "500px", // Ensure enough height
              borderRadius: "15px",
              overflow: "auto", // Enables scrolling if content overflows
              width: "100%", // Ensures responsiveness
            }}
          >
            <Grid
              container
              spacing={1}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                margin: "auto",
              }}
            >
              <Grid item xs={12} md={12} lg={10}>
                <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                  <div style={{ position: "relative", width: "100%" }}>
                    <StyledTypography>
                      {editMode ? "Edit Leave Application" : "Apply Leave"}
                    </StyledTypography>
                    <Tooltip title="Reset">
                      <StyledButtonC onClick={resetForm}>
                        <RestartAltIcon />
                      </StyledButtonC>
                    </Tooltip>
                  </div>
                  <Grid sx={{ padding: 3 }}>
                    <form onSubmit={handleSubmit} autoComplete="off">
                      <Grid container spacing={4}>
                        {/* Left Column */}
                        <Grid item xs={12} md={6}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Autocomplete
                                options={employees}
                                getOptionLabel={(option) =>
                                  (option.employeeName || "Unnamed Employee")?.trim() || ""
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.employeeRecordId === value?.employeeRecordId
                                }
                                value={
                                  employees.find((e) => e.employeeRecordId === employeeRecordId) ||
                                  null
                                }
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    setEmployeeName(newValue.employeeName);
                                    setEmployeeRecordId(newValue.employeeRecordId);
                                  } else {
                                    setEmployeeName("");
                                    setEmployeeRecordId(null);
                                  }
                                }}
                                loading={loadingEmployees}
                                PaperComponent={CustomPaper}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Employee Name"
                                    required
                                    sx={MInput.b}
                                    InputLabelProps={{ sx: { color: MInput.a } }}
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {loadingEmployees ? (
                                            <CircularProgress color="inherit" size={20} />
                                          ) : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Autocomplete
                                options={leaveTypes}
                                getOptionLabel={(option) => option.label}
                                value={leaveTypes.find((type) => type.value === leaveType) || null}
                                onChange={(event, value) => setLeaveType(value ? value.value : "")}
                                PaperComponent={CustomPaper}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Select Leave Type"
                                    required
                                    sx={{
                                      height: 43,
                                      ...MInput.b,
                                      "& .MuiInputBase-root": {
                                        height: "100%",
                                        minHeight: 43,
                                      },
                                    }}
                                    InputLabelProps={{
                                      sx: {
                                        color: MInput.a,
                                      },
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                label="Balance"
                                value={leaveBalance}
                                fullWidth
                                disabled
                                sx={MInput.b}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                label="Remarks"
                                multiline
                                rows={3}
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                fullWidth
                                sx={MInput.b}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* Right Column */}
                        <Grid item xs={12} md={6}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                label="Current Date"
                                value={new Date().toLocaleDateString()}
                                fullWidth
                                disabled
                                sx={MInput.b}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                  label="From Date"
                                  value={fromDate}
                                  onChange={(newValue) => {
                                    if (toDate && newValue > toDate) {
                                      setAlert("From Date cannot be greater than To Date", "error");
                                      return;
                                    }
                                    setFromDate(newValue);
                                  }}
                                  minDate={new Date()}
                                  inputFormat="dd-MM-yyyy"
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      required
                                      fullWidth
                                      sx={MInput.b}
                                      error={false}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                  label="To Date"
                                  value={toDate}
                                  onChange={(newValue) => {
                                    if (fromDate && newValue < fromDate) {
                                      setAlert("To Date cannot be less than From Date", "error");
                                      return;
                                    }
                                    setToDate(newValue);
                                  }}
                                  minDate={fromDate || new Date()}
                                  inputFormat="dd-MM-yyyy"
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      required
                                      fullWidth
                                      sx={MInput.b}
                                      error={false}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                label="Number of Days"
                                value={numberOfDays}
                                fullWidth
                                disabled
                                sx={MInput.b}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                                <StyledButton type="submit" disabled={loading || submitting}>
                                  {submitting ? "Processing..." : editMode ? "Update" : "Submit"}
                                </StyledButton>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </form>
                  </Grid>
                </TableContainer>
              </Grid>
            </Grid>
          </StyledTableContainer>
        )}
      </Box>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
      <Dialog
        open={remarksDialogOpen}
        onClose={() => setRemarksDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Leave Remarks</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-wrap", mt: 2 }}>
            {selectedRemarks || "No remarks provided"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ViewButton onClick={() => setRemarksDialogOpen(false)}>Close</ViewButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={dialogContentTextStyle}>
            Are you sure you want to delete this leave application?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ViewButton onClick={handleCloseDialog} color="primary">
            Cancel
          </ViewButton>
          <ViewButton onClick={handleDeleteConfirm} color="error">
            Delete
          </ViewButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

EmployeeLeave.propTypes = {
  data: PropTypes.shape({
    status: PropTypes.string.isRequired, // Validate the 'status' field
  }).isRequired, // 'data' prop is required
};
